import { Suspense } from 'react';
import axios from 'axios';
import ReactDOM from 'react-dom';
import { configureAxiosDefaults } from '@hotjar/webapp/utils/axios-config';
import { measureAndLog } from '@hotjar/performance';
import { MARKS } from '@hotjar/webapp/performance';
import { ErrorBoundaryForPages } from '@hotjar/error-handler';
import { initI18Next } from '@hotjar/translations';
import { noop } from '@hotjar/webapp/lib/function';
import wdyr from './utils/WhyDidYouRender';

import { App } from './App';
import { AppProviders } from './AppProviders';

initI18Next();

if (process.env.NODE_ENV === 'development') {
  import('@hotjar/webapp/api-mocks/browser').then(({ weakStart }) => {
    // necessary for history-based routing
    const options = {
      serviceWorker: { url: '/mockServiceWorker.js' },
      onUnhandledRequest: 'bypass',
    } as const;
    weakStart(options);
  });
}

// Only available for `process.env.NODE_ENV=development` and if `wdyr-enabled=1` is found in localStorage
wdyr();

// Perf: Mark application loading timings
measureAndLog({ name: MARKS.APP_LOADED, addMark: true });

configureAxiosDefaults();

// Trigger a request to confirm that we are not a sanctioned country and are allowed to continue
// The http::451 response code will be picked up and handled by the axios interceptor.
// This redirects to an external hotjar 451 page so it doesn't need to interact with the react app in any way.
axios.get('/is-allowed').then(noop, noop);

const rootElement = document.getElementById('root');

ReactDOM.render(
  <Suspense fallback={null}>
    <AppProviders>
      <ErrorBoundaryForPages squadSentryTag="hotjar" criticality="critical" featureName="Root page">
        <App />
      </ErrorBoundaryForPages>
    </AppProviders>
  </Suspense>,
  rootElement,

  // Perf: Mark application initialization timings
  () =>
    measureAndLog({
      name: MARKS.APP_INITIALIZED,
      startMark: MARKS.APP_LOADED,
      addMark: true,
    }),
);
